/**
 * WeaveIn.ai Platform - Program Contact Status Configuration
 * Admin page for configuring per-program contact lifecycle pipelines
 */

import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, GripVertical, Save, X, Loader2, CheckCircle,
  Palette, ArrowRight, Lock, Wand2, ChevronDown, ChevronUp
} from 'lucide-react';
import { contactsAPI, StatusConfigItem, StatusConfigCreate } from '@/services/api/contacts';
import { workflowsAPI } from '@/services/api/workflows';

import { config as productConfig } from '@/src/config';
interface ProgramContactStatusConfigProps {
  programId: number;
  programName?: string;
}

const AVAILABLE_FIELDS = [
  'first_name', 'last_name', 'phone_no', 'email', 'dob', 'gender',
  'race', 'ethnicity', 'language', 'street_address', 'city', 'state', 'zip_code',
  'medicaid_id', 'medicare_id',
];

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name', last_name: 'Last Name', phone_no: 'Phone', email: 'Email',
  dob: 'Date of Birth', gender: 'Gender', race: 'Race', ethnicity: 'Ethnicity',
  language: 'Language', street_address: 'Street', city: 'City', state: 'State',
  zip_code: 'Zip Code', medicaid_id: 'Medicaid ID', medicare_id: 'Medicare ID',
};

const STATUS_COLORS = [
  '#3B82F6', '#6366F1', '#8B5CF6', '#F59E0B', '#10B981',
  '#059669', '#EF4444', '#6B7280', '#EC4899', '#14B8A6',
];

export default function ProgramContactStatusConfig({ programId, programName }: ProgramContactStatusConfigProps) {
  const [configs, setConfigs] = useState<StatusConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Picker options loaded from APIs
  const [docTypes, setDocTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [billingActivities, setBillingActivities] = useState<Array<{ id: number; name: string }>>([]);
  const [commTemplates, setCommTemplates] = useState<Array<{ id: number; name: string }>>([]);
  const [workflowTemplates, setWorkflowTemplates] = useState<Array<{ id: number; name: string }>>([]);

  const [formData, setFormData] = useState<StatusConfigCreate>({
    status_code: '',
    status_label: '',
    sort_order: 0,
    status_color: '#3B82F6',
    is_terminal: false,
    promotes_to_enrollment: false,
    required_member_fields: [],
    required_identifier_types: [],
    allowed_next_statuses: [],
  });

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await contactsAPI.getStatusConfig(programId);
      setConfigs(data.sort((a, b) => a.sort_order - b.sort_order));
    } catch {
      setError('Failed to load status configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfigs(); }, [programId]);

  // Load picker options
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    const base = (productConfig.weaveinApi.baseUrl ?? '').trim().replace(/\/$/, '');
    const headers = { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId || '' };

    // Doc types
    fetch(`${base}/api/doc-types?program_id=${programId}`, { headers })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setDocTypes(Array.isArray(data) ? data.map((d: any) => ({ id: d.id, name: d.name })) : []))
      .catch(() => {});

    // Billing activities
    fetch(`${base}/api/billing-activities?program_id=${programId}`, { headers })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const items = Array.isArray(data) ? data : data?.activities || [];
        setBillingActivities(items.map((a: any) => ({ id: a.id, name: a.activity_name || a.activity_code || `#${a.id}` })));
      })
      .catch(() => {});

    // Communication templates
    fetch(`${base}/api/communication-templates`, { headers })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const items = Array.isArray(data) ? data : data?.templates || [];
        setCommTemplates(items.map((t: any) => ({ id: t.id, name: t.name })));
      })
      .catch(() => {});

    // Workflow templates (contact-targeting)
    workflowsAPI.listWorkflowTemplates({ program_id: programId, is_active: true, target_entity_type: 'contact' })
      .then((res) => setWorkflowTemplates((res.workflows || []).map((w: any) => ({ id: w.id, name: w.name }))))
      .catch(() => {});
  }, [programId]);

  const handleSeedDefaults = async () => {
    setSaving(true);
    try {
      await contactsAPI.seedDefaultStatusConfig(programId);
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to seed defaults');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (config: StatusConfigItem) => {
    setEditingId(config.id);
    setFormData({
      status_code: config.status_code,
      status_label: config.status_label,
      status_description: config.status_description,
      sort_order: config.sort_order,
      status_color: config.status_color || '#3B82F6',
      is_terminal: config.is_terminal,
      promotes_to_enrollment: config.promotes_to_enrollment,
      required_member_fields: config.required_member_fields || [],
      required_identifier_types: config.required_identifier_types || [],
      allowed_next_statuses: config.allowed_next_statuses || [],
    });
    setShowCreate(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setFormData({
      status_code: '',
      status_label: '',
      sort_order: configs.length,
      status_color: STATUS_COLORS[configs.length % STATUS_COLORS.length],
      is_terminal: false,
      promotes_to_enrollment: false,
      required_member_fields: [],
      allowed_next_statuses: [],
    });
    setShowCreate(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await contactsAPI.updateStatusConfig(programId, editingId, formData);
      } else {
        await contactsAPI.createStatusConfig(programId, formData);
      }
      setEditingId(null);
      setShowCreate(false);
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId: number) => {
    if (!confirm('Delete this status? Only possible if no contacts are in this status.')) return;
    try {
      await contactsAPI.deleteStatusConfig(programId, configId);
      await loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleField = (field: string) => {
    setFormData((prev) => ({
      ...prev,
      required_member_fields: prev.required_member_fields?.includes(field)
        ? prev.required_member_fields.filter((f) => f !== field)
        : [...(prev.required_member_fields || []), field],
    }));
  };

  const toggleNextStatus = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      allowed_next_statuses: prev.allowed_next_statuses?.includes(code)
        ? prev.allowed_next_statuses.filter((s) => s !== code)
        : [...(prev.allowed_next_statuses || []), code],
    }));
  };

  if (loading) {
    return <div className="flex items-center gap-2 py-8 justify-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Contact Status Pipeline</h3>
          {programName && <p className="text-sm text-gray-500">{programName}</p>}
        </div>
        <div className="flex gap-2">
          {configs.length === 0 && (
            <button
              onClick={handleSeedDefaults}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg hover:bg-indigo-200"
            >
              <Wand2 className="w-4 h-4" /> Seed Defaults
            </button>
          )}
          <button
            onClick={startCreate}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> Add Status
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Status list */}
      <div className="space-y-2">
        {configs.map((config) => (
          <div
            key={config.id}
            className={`border rounded-lg transition-all ${
              editingId === config.id
                ? 'border-blue-400 dark:border-blue-600 shadow-sm'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: config.status_color || '#6B7280' }}
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900 dark:text-gray-100">{config.status_label}</span>
                <span className="ml-2 text-xs text-gray-400 font-mono">{config.status_code}</span>
              </div>
              <div className="flex items-center gap-2">
                {config.is_terminal && (
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded">Terminal</span>
                )}
                {config.promotes_to_enrollment && (
                  <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Promotes</span>
                )}
                {config.required_member_fields && config.required_member_fields.length > 0 && (
                  <span className="text-xs text-gray-400">{config.required_member_fields.length} fields</span>
                )}
                <button onClick={() => startEdit(config)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <Edit className="w-4 h-4 text-gray-400" />
                </button>
                <button onClick={() => handleDelete(config.id)} className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>

            {/* Allowed transitions */}
            {config.allowed_next_statuses && config.allowed_next_statuses.length > 0 && (
              <div className="px-4 pb-2 flex items-center gap-1 text-xs text-gray-400">
                <ArrowRight className="w-3 h-3" />
                {config.allowed_next_statuses.join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create / Edit Form */}
      {(showCreate || editingId) && (
        <div className="p-4 border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {editingId ? 'Edit Status' : 'New Status'}
          </h4>

          <div className="grid grid-cols-2 gap-3">
            {!editingId && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code (lowercase, underscores)</label>
                <input
                  value={formData.status_code}
                  onChange={(e) => setFormData((p) => ({ ...p, status_code: e.target.value.toLowerCase().replace(/[^a-z_]/g, '') }))}
                  className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                  placeholder="e.g. qualified"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
              <input
                value={formData.status_label}
                onChange={(e) => setFormData((p) => ({ ...p, status_label: e.target.value }))}
                className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="Display name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
              <div className="flex gap-1">
                {STATUS_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, status_color: c }))}
                    className={`w-6 h-6 rounded-full border-2 ${formData.status_color === c ? 'border-gray-900 dark:border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_terminal}
                onChange={(e) => setFormData((p) => ({ ...p, is_terminal: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Terminal (end state)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.promotes_to_enrollment}
                onChange={(e) => setFormData((p) => ({ ...p, promotes_to_enrollment: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Promotes to Enrollment
            </label>
          </div>

          {/* Required fields */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Required Fields at This Status</label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_FIELDS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggleField(f)}
                  className={`px-2 py-1 text-xs rounded border transition-all ${
                    formData.required_member_fields?.includes(f)
                      ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {FIELD_LABELS[f] || f}
                </button>
              ))}
            </div>
          </div>

          {/* Allowed next statuses */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Allowed Transitions To</label>
            <div className="flex flex-wrap gap-1.5">
              {configs
                .filter((c) => c.status_code !== formData.status_code)
                .map((c) => (
                  <button
                    key={c.status_code}
                    type="button"
                    onClick={() => toggleNextStatus(c.status_code)}
                    className={`px-2 py-1 text-xs rounded border transition-all ${
                      formData.allowed_next_statuses?.includes(c.status_code)
                        ? 'border-green-500 bg-green-100 dark:bg-green-900/30 text-green-700'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {c.status_label}
                  </button>
                ))}
            </div>
          </div>

          {/* Contact Type Extensions */}
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Contact Type Integration</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact Type Label</label>
                <input
                  value={(formData as any).contact_type_label || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, contact_type_label: e.target.value } as any))}
                  className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                  placeholder="e.g. ECM Referral, CHW Walk-in"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Required Document Types</label>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {docTypes.length > 0 ? docTypes.map((dt) => (
                    <button key={dt.id} type="button"
                      onClick={() => setFormData((p) => {
                        const ids = (p as any).required_document_type_ids || [];
                        return { ...p, required_document_type_ids: ids.includes(dt.id) ? ids.filter((i: number) => i !== dt.id) : [...ids, dt.id] } as any;
                      })}
                      className={`px-2 py-1 text-xs rounded border ${((formData as any).required_document_type_ids || []).includes(dt.id) ? 'border-blue-500 bg-blue-100 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                      {dt.name}
                    </button>
                  )) : <span className="text-xs text-gray-400">No document types configured</span>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Billing Activities</label>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {billingActivities.length > 0 ? billingActivities.map((ba) => (
                    <button key={ba.id} type="button"
                      onClick={() => setFormData((p) => {
                        const ids = (p as any).billing_activity_ids || [];
                        return { ...p, billing_activity_ids: ids.includes(ba.id) ? ids.filter((i: number) => i !== ba.id) : [...ids, ba.id] } as any;
                      })}
                      className={`px-2 py-1 text-xs rounded border ${((formData as any).billing_activity_ids || []).includes(ba.id) ? 'border-green-500 bg-green-100 text-green-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                      {ba.name}
                    </button>
                  )) : <span className="text-xs text-gray-400">No billing activities configured</span>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Auto-Send Communication Template</label>
                <select
                  value={(formData as any).auto_send_communication_template_id || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, auto_send_communication_template_id: e.target.value ? parseInt(e.target.value) : undefined } as any))}
                  className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                  <option value="">None</option>
                  {commTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Auto-Create Workflow</label>
                <select
                  value={(formData as any).auto_create_workflow_template_id || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, auto_create_workflow_template_id: e.target.value ? parseInt(e.target.value) : undefined } as any))}
                  className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                  <option value="">None</option>
                  {workflowTemplates.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setEditingId(null); setShowCreate(false); }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.status_label}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
